import React, { useState } from 'react';
import { Hammer, Layers, Trees, Flower2, Lightbulb, Footprints, PenTool, Scissors, Wrench, ArrowRight, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface FeaturedService {
  title: string;
  desc: string;
  image: string;
  icon: React.ReactNode;
  span: string;
}
interface SmallService {
  title: string;
  desc: string;
  icon: React.ReactNode;
}
export function ServicesSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const featuredServices: FeaturedService[] = [{
    title: 'Landscaping & Mulch',
    desc: 'Full-property designs with fresh mulch beds that pop with color and life. We transform your curb appeal from the ground up.',
    image: 'https://cdn.magicpatterns.com/uploads/t7Rvo6gdSDXRCvfsZChTTB/IA2.png',
    icon: <Trees className="w-6 h-6" data-id="element-292" />,
    span: 'md:col-span-2 md:row-span-2'
  }, {
    title: 'Pavers & Stone Work',
    desc: 'Elegant raised beds, retaining walls, and decorative stone features that boost curb appeal instantly.',
    image: 'https://cdn.magicpatterns.com/uploads/24UySDmdddB1mQ8Q29RSD2/IA3.png',
    icon: <Layers className="w-6 h-6" data-id="element-293" />,
    span: 'md:col-span-1 md:row-span-1'
  }, {
    title: 'Patios & Outdoor Living',
    desc: 'Custom patios, gazebos, and outdoor living spaces your family will actually use — built to last.',
    image: 'https://cdn.magicpatterns.com/uploads/43fPmT8KtNiy1PpTcx9xVK/IA4.png',
    icon: <Footprints className="w-6 h-6" data-id="element-294" />,
    span: 'md:col-span-1 md:row-span-1'
  }, {
    title: 'Retaining Walls & Planting',
    desc: 'Beautiful curved retaining walls with professionally designed plant beds that turn heads.',
    image: 'https://cdn.magicpatterns.com/uploads/fENgsHockHrRwgMZ661JcB/IA5.png',
    icon: <Flower2 className="w-6 h-6" data-id="element-295" />,
    span: 'md:col-span-2 md:row-span-1'
  }];
  const smallServices: SmallService[] = [{
    title: 'Concrete',
    desc: 'Driveways, slabs & steps built to last',
    icon: <Hammer className="w-6 h-6" data-id="element-296" />
  }, {
    title: 'Outdoor Lighting',
    desc: 'Your home glowing beautifully every night',
    icon: <Lightbulb className="w-6 h-6" data-id="element-297" />
  }, {
    title: 'Landscape Design',
    desc: "A complete vision for your property's potential",
    icon: <PenTool className="w-6 h-6" data-id="element-298" />
  }, {
    title: 'Lawn Care',
    desc: 'Clean, maintained, always sharp',
    icon: <Scissors className="w-6 h-6" data-id="element-299" />
  }];
  return <section id="services" className="py-24 bg-[#0F2B1E]" data-id="element-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-301">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-302">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-303">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-[#D4A373] mb-6" data-id="element-304">
              <Wrench size={16} data-id="element-305" />
              <span className="text-sm font-semibold tracking-wider uppercase" data-id="element-306">
                Our Work
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4" data-id="element-307">
              Our Complete Services
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto" data-id="element-308">
              Real results from real projects across Northeast Ohio.
              <br data-id="element-309" />
              <span className="text-white/40 text-base" data-id="element-310">
                If it's outside your home — we handle it.
              </span>
            </p>
          </motion.div>
        </div>

        {/* Bento Grid - Featured Services with Photos */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-311">
          {featuredServices.map((service, index) => <motion.div key={index} className={`relative rounded-2xl overflow-hidden cursor-pointer group ${service.span}`} style={{
          minHeight: index === 0 ? '480px' : '240px'
        }} onMouseEnter={() => setHoveredIndex(index)} onMouseLeave={() => setHoveredIndex(null)} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-312">
              {/* Background Image */}
              <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-313" />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent transition-all duration-300 group-hover:from-black/90 group-hover:via-black/50" data-id="element-314" />

              {/* Red accent line at top */}
              <div className="absolute top-0 left-0 w-full h-1 bg-[#C41E24] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" data-id="element-315" />

              {/* Content */}
              <div className="absolute inset-0 p-6 flex flex-col justify-end" data-id="element-316">
                <div className="flex items-center gap-3 mb-3" data-id="element-317">
                  <div className="w-10 h-10 rounded-lg bg-[#C41E24] text-white flex items-center justify-center shadow-lg" data-id="element-318">
                    {service.icon}
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-319">
                    {service.title}
                  </h3>
                </div>

                <AnimatePresence data-id="element-320">
                  {hoveredIndex === index && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.3
              }} data-id="element-321">
                      <p className="text-white/80 text-sm md:text-base mb-4 max-w-lg leading-relaxed" data-id="element-322">
                        {service.desc}
                      </p>
                      <a href="tel:4402221065" className="inline-flex items-center gap-2 text-[#D4A373] font-semibold text-sm hover:text-white transition-colors" data-id="element-323">
                        <span data-id="element-324">Get a Free Quote</span>
                        <ArrowRight size={16} data-id="element-325" />
                      </a>
                    </motion.div>}
                </AnimatePresence>

                {/* Always visible on mobile */}
                <div className="md:hidden" data-id="element-326">
                  <p className="text-white/80 text-sm mb-3 leading-relaxed" data-id="element-327">
                    {service.desc}
                  </p>
                  <a href="tel:4402221065" className="inline-flex items-center gap-2 text-[#D4A373] font-semibold text-sm" data-id="element-328">
                    <span data-id="element-329">Get a Free Quote</span>
                    <ArrowRight size={16} data-id="element-330" />
                  </a>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Small Services Grid */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-331">
          {smallServices.map((service, index) => <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-5 group hover:bg-white/10 transition-all duration-300 hover:border-[#C41E24]/30" data-id="element-332">
              <div className="w-12 h-12 rounded-xl bg-[#C41E24]/15 text-[#C41E24] flex items-center justify-center mb-4 group-hover:bg-[#C41E24] group-hover:text-white transition-all duration-300" data-id="element-333">
                {service.icon}
              </div>
              <h3 className="text-base font-bold text-white mb-1" data-id="element-334">
                {service.title}
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-335">
                {service.desc}
              </p>
            </div>)}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="text-center mt-12" data-id="element-336">
          <a href="tel:4402221065" className="inline-flex items-center gap-3 bg-[#C41E24] hover:bg-[#a8181e] text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-[#C41E24]/20" data-id="element-337">
            <Phone size={20} data-id="element-338" />
            <span data-id="element-339">Discuss Your Project — Call Now</span>
          </a>
        </motion.div>
      </div>
    </section>;
}